import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import {
  applySearchResultsVisibility,
  countVisibleCards,
  parseVisibilityPayloadFromSectionHTML,
} from '@theme/search-results-visibility';

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {boolean} */
  #loadingMore = false;

  /** @type {boolean} */
  #autoFetching = false;

  /** Last page fetched (or attempted). Used so empty pages do not block load more. */
  #lastFetchedPage = 1;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.dataset.loadMore === 'true') {
      this.#lastFetchedPage = this.#getHighestPageFromDom();
      this.#normalizeSearchUrlOnLoad();
      this.#setupLoadMoreButton();
      this.#updateLoadMoreButton();
      document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdateLoadMore);
      document.addEventListener('isbn-visibility-complete', this.#handleVisibilityComplete);
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  resolvePage(type) {
    if (this.dataset.loadMore !== 'true' || type !== 'next') return undefined;

    const { grid } = this.refs;
    const lastPage = Number(grid?.dataset.lastPage || 1);
    const nextPage = this.#lastFetchedPage + 1;

    if (nextPage > lastPage) return undefined;

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(nextPage));
    url.hash = '';

    return { page: nextPage, url };
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdateLoadMore);
    document.removeEventListener('isbn-visibility-complete', this.#handleVisibilityComplete);
  }

  #handleVisibilityComplete = async () => {
    if (this.dataset.loadMore !== 'true') return;
    await this.#autoFetchUntilTarget();
  };

  async loadNextPage() {
    const pageNumber = this.#getUpcomingPageNumber();
    if (!pageNumber) return false;

    const { grid } = this.refs;
    const countBefore = grid?.querySelectorAll(':scope > [ref="cards[]"]').length ?? 0;

    await super.loadNextPage();

    // Always advance so empty/filtered pages cannot stall pagination on the same page number.
    this.#lastFetchedPage = pageNumber;

    const countAfter = grid?.querySelectorAll(':scope > [ref="cards[]"]').length ?? 0;
    const cardsAppended = countAfter > countBefore;

    if (cardsAppended) {
      await this.#applyVisibilityForNewlyLoadedPage(pageNumber);
      this.#keepUncheckedAdoptedTitleCardsHidden();
    }

    this.#updateLoadMoreButton();

    return cardsAppended;
  }

  /**
   * adoptedtitle cards without a completed check stay hidden (fail-safe).
   */
  #keepUncheckedAdoptedTitleCardsHidden() {
    this.querySelectorAll(
      '[data-requires-adoption-check="true"]:not([data-pdp-visibility-checked])'
    ).forEach((card) => {
      card.setAttribute('data-pdp-visibility-pending', 'true');
    });
  }

  #handleFilterUpdateLoadMore = () => {
    requestIdleCallback(() => {
      this.#lastFetchedPage = this.#getHighestPageFromDom();
      this.#autoFetching = false;
      this.#loadingMore = false;
      this.#updateLoadMoreButton();
    });
  };

  #setupLoadMoreButton() {
    const { loadMoreButton } = this.refs;
    if (!(loadMoreButton instanceof HTMLButtonElement)) return;

    loadMoreButton.addEventListener('click', this.#handleLoadMoreClick);
  }

  #handleLoadMoreClick = async () => {
    if (this.#loadingMore || this.#autoFetching || !this.#shouldShowLoadMoreButton()) return;

    this.#loadingMore = true;
    this.#updateLoadMoreButton();

    try {
      const target = this.#countVisibleCards() + this.#getTargetVisibleCount();
      await this.#fetchUntilVisibleTarget(target);
    } finally {
      this.#loadingMore = false;
      this.#updateLoadMoreButton();
    }
  };

  async #autoFetchUntilTarget() {
    if (this.#autoFetching || this.#loadingMore) return;

    const target = this.#getTargetVisibleCount();
    if (this.#countVisibleCards() >= target || !this.#hasMorePages()) {
      this.#updateLoadMoreButton();
      return;
    }

    this.#autoFetching = true;
    this.#updateLoadMoreButton();

    try {
      await this.#fetchUntilVisibleTarget(target);
    } finally {
      this.#autoFetching = false;
      this.#updateLoadMoreButton();
    }
  }

  /**
   * @param {number} target
   */
  async #fetchUntilVisibleTarget(target) {
    const maxPages = Number(this.dataset.maxPagesPerLoadMore || 50);
    let pagesAttempted = 0;

    while (this.#countVisibleCards() < target && this.#hasMorePages() && pagesAttempted < maxPages) {
      pagesAttempted += 1;
      await this.loadNextPage();
    }
  }

  async #normalizeSearchUrlOnLoad() {
    const url = new URL(window.location.href);
    const pageParam = url.searchParams.get('page');

    if (!pageParam) return;

    url.searchParams.delete('page');
    history.replaceState('', '', url.toString());

    if (Number(pageParam) > 1) {
      await this.#resetToFirstPage(url);
    }
  }

  /**
   * @param {URL} pageOneUrl - Current location without a page param
   */
  async #resetToFirstPage(pageOneUrl) {
    const { grid } = this.refs;
    if (!grid) return;

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, pageOneUrl);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[ref="grid"]');

      if (!newGrid) return;

      grid.replaceChildren(...newGrid.children);

      if (newGrid.dataset.lastPage) {
        grid.dataset.lastPage = newGrid.dataset.lastPage;
      }

      if (newGrid.dataset.pageSize) {
        grid.dataset.pageSize = newGrid.dataset.pageSize;
      }

      this.pages.clear();
      this.#lastFetchedPage = 1;

      const payloads = parseVisibilityPayloadFromSectionHTML(html);
      if (payloads?.length) {
        await applySearchResultsVisibility(this, payloads, { source: 'url-reset-page-1' });
      }

      await this.#autoFetchUntilTarget();
    } catch (error) {
      console.error('Unable to reset search results to page 1', error);
    }
  }

  #isPaginationExhausted() {
    const { grid } = this.refs;
    if (!grid) return true;

    const lastPage = Number(grid.dataset.lastPage || 1);
    return this.#lastFetchedPage >= lastPage;
  }

  #getTargetVisibleCount() {
    const { grid } = this.refs;
    return Number(grid?.dataset.pageSize || 24);
  }

  #shouldShowLoadMoreButton() {
    return this.#countVisibleCards() >= this.#getTargetVisibleCount() && this.#hasMorePages();
  }

  #updateLoadMoreButton() {
    const { loadMoreButton, loadMoreWrapper, loadMoreSpinner } = this.refs;

    if (!(loadMoreWrapper instanceof HTMLElement)) return;

    const loading = this.#loadingMore || this.#autoFetching;
    const showButton = !loading && this.#shouldShowLoadMoreButton();
    const showSpinner = loading;
    const showWrapper = showButton || showSpinner;

    loadMoreWrapper.hidden = !showWrapper;
    loadMoreWrapper.classList.toggle('main-collection-load-more--exhausted', !showWrapper);

    if (loadMoreButton instanceof HTMLButtonElement) {
      loadMoreButton.hidden = !showButton;
      loadMoreButton.disabled = !showButton;
      loadMoreButton.setAttribute('aria-disabled', showButton ? 'false' : 'true');
      loadMoreButton.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    if (loadMoreSpinner instanceof HTMLElement) {
      loadMoreSpinner.hidden = !showSpinner;
      loadMoreSpinner.setAttribute('aria-busy', showSpinner ? 'true' : 'false');
      loadMoreSpinner.setAttribute('aria-hidden', showSpinner ? 'false' : 'true');
    }
  }

  #hasMorePages() {
    return !this.#isPaginationExhausted();
  }

  #countVisibleCards() {
    return countVisibleCards(this);
  }

  #getUpcomingPageNumber() {
    const { grid } = this.refs;
    const lastPage = Number(grid?.dataset.lastPage || 1);
    const nextPage = this.#lastFetchedPage + 1;

    if (nextPage > lastPage) return undefined;

    return nextPage;
  }

  #getHighestPageFromDom() {
    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return 1;

    return cards.reduce((max, card) => {
      const page = Number(card.dataset.page) || 1;
      return page > max ? page : max;
    }, 1);
  }

  async #applyVisibilityForNewlyLoadedPage(pageNumber) {
    const pageHTML = pageNumber ? this.getPageHTML(pageNumber) : undefined;
    const payloads = pageHTML ? parseVisibilityPayloadFromSectionHTML(pageHTML) : null;

    if (!payloads?.length) {
      return;
    }

    await applySearchResultsVisibility(this, payloads, {
      source: pageNumber ? `load-more-page-${pageNumber}` : 'load-more',
    });
  }

  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
